import React, { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { HeroSection } from "@/components/sections/HeroSection";
import { FeaturesSection } from "@/components/sections/FeaturesSection";
import { CTASection } from "@/components/sections/CTASection";
import { ChatModal } from "@/components/ChatModal";
import { AccessGate } from "@/components/AccessGate";
import { AdminPanel } from "@/components/AdminPanel";
import { Toaster } from "@/components/ui/sonner";

function App() {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [openToHistory, setOpenToHistory] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminCode, setAdminCode] = useState('');

  // Check for existing session on mount
  useEffect(() => {
    const storedAccess = sessionStorage.getItem('bunkerAccess');
    if (storedAccess) {
      try {
        const access = JSON.parse(storedAccess);
        // Check if session is still valid (24 hours)
        if (access.valid && (Date.now() - access.timestamp) < 24 * 60 * 60 * 1000) {
          setHasAccess(true);
          setIsAdmin(access.isAdmin);
          if (access.code) {
            setAdminCode(access.code);
          }
        } else {
          sessionStorage.removeItem('bunkerAccess');
        }
      } catch (e) {
        sessionStorage.removeItem('bunkerAccess');
      }
    }
  }, []);

  const handleAccessGranted = useCallback((isAdminUser, code) => {
    setHasAccess(true);
    setIsAdmin(isAdminUser);
    if (code) {
      setAdminCode(code);
    }
  }, []);

  const openChat = useCallback(() => {
    setOpenToHistory(false);
    setIsChatOpen(true);
  }, []);

  const openHistory = useCallback(() => {
    setOpenToHistory(true);
    setIsChatOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setOpenToHistory(false);
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('bunkerAccess');
    setHasAccess(false);
    setIsAdmin(false);
    setShowAdminPanel(false);
    setAdminCode('');
  }, []);

  // Show access gate if not authenticated
  if (!hasAccess) {
    return <AccessGate onAccessGranted={handleAccessGranted} />;
  }

  // Show admin panel if admin and requested
  if (showAdminPanel && isAdmin) {
    return (
      <AdminPanel 
        adminCode={adminCode}
        onBack={() => setShowAdminPanel(false)}
      />
    );
  }

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Header 
        onOpenChat={openChat} 
        onOpenHistory={openHistory}
        isAdmin={isAdmin}
        onOpenAdmin={() => setShowAdminPanel(true)}
        onLogout={handleLogout}
      />
      
      <main>
        <HeroSection onOpenChat={openChat} />
        <FeaturesSection />
        <CTASection onOpenChat={openChat} />
      </main>
      
      <Footer />
      <ChatModal isOpen={isChatOpen} onClose={closeChat} userCode={adminCode} initialShowHistory={openToHistory} />
      <Toaster />
    </div>
  );
}

export default App;
